/* eslint-disable react/require-default-props */
import React from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import parse from "html-react-parser";
import { Button, FlexColumn, FlexRow, Typography } from "design-system";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  LockIcon,
} from "design-system/components/Icons";
import QuizCardShell, { QuizPassMarkNote } from "../../components/QuizCardShell";
import SurveyQuestionInput from "../../../Survey/pages/ClientConnectV2/survey/SurveyQuestionInput";
import SurveyQuestionFieldError from "../../../Survey/pages/ClientConnectV2/survey/SurveyQuestionFieldError";
import { SurveyPreviewProvider } from "../../../Survey/pages/ClientConnectV2/survey/SurveyPreviewContext";
import { questionTypes } from "../../../Survey/pages/ClientConnectV2/survey/SurveyPreviewBody";
import { normalizeId } from "../../../Survey/pages/ClientConnectV2/survey/surveyPreviewUtils";

function questionUsesInputErrorState(questionType) {
  return [
    questionTypes.TEXT,
    questionTypes.NUMBER,
    questionTypes.DROPDOWN,
    questionTypes.INVENTORY,
  ].includes(questionType);
}

export default function QuizQuestionCard({
  question,
  questionIndex,
  totalQuestions,
  answers,
  onSetAnswer,
  onFinishSurvey,
  onNext,
  onBack,
  canGoBack,
  canProceed,
  submitting = false,
  isLastQuestion,
  passMarkPercent,
  surveyErrors = {},
  onSetQuestionError,
  employeesLoading = false,
  survey = null,
}) {
  const intl = useIntl();
  const questionNumber = questionIndex + 1;
  const nextLabel = isLastQuestion
    ? (submitting
      ? intl.formatMessage({ id: 'ld-submitting' })
      : intl.formatMessage({ id: 'ld-submit-answers' }))
    : intl.formatMessage({ id: 'next-question' });

  const errorMessage =
    surveyErrors[normalizeId(question._id)]?.message ??
    surveyErrors[question._id]?.message;
  const hasError = Boolean(errorMessage);
  const usesInputError = questionUsesInputErrorState(question.questionType);

  const multiSelectLimitHandlers = onSetQuestionError
    ? {
        onMinError: () => {
          onSetQuestionError(question._id, {
            message: intl.formatMessage(
              { id: 'select-atleast-min-options' },
              { minOptions: question.min_options },
            ),
          });
        },
        onMaxError: () => {
          onSetQuestionError(question._id, {
            message: intl.formatMessage(
              { id: 'select-max-options' },
              { maxOptions: question.max_options },
            ),
          });
        },
        onClearErrors: () => {
          onSetQuestionError(question._id, null);
        },
      }
    : {};


  const footerStart = canGoBack ? (
    <Button
      variant="link"
      size="base"
      className="ld-quiz__back"
      icon={<ArrowLeftIcon size={11} />}
      iconPosition="left"
      onClick={onBack}
    >
      {intl.formatMessage({ id: 'back' })}
    </Button>
  ) : canProceed ? (
    <Typography component="span" variant="bodySmall" color="ui-subtle">
      {isLastQuestion
        ? intl.formatMessage({ id: 'ld-ready-to-submit' })
        : intl.formatMessage({ id: 'ld-answer-selected' })}
    </Typography>
  ) : (
    <FlexRow align="center" fullWidth={false} gap="var(--ds-space-2)">
      <LockIcon size={16} className="ds-icon--color-muted" aria-hidden />
      <Typography component="span" variant="bodySmall" color="ui-subtle">
        {intl.formatMessage({ id: 'ld-select-answer' })}
      </Typography>
    </FlexRow>
  );

  const footerAction = (
    <Button
      variant="primary"
      size="base"
      className="ld-guidance-step__continue"
      icon={<ArrowRightIcon size={11} />}
      iconPosition="right"
      disabled={!canProceed || submitting}
      onClick={onNext}
    >
      {nextLabel}
    </Button>
  );

  return (
    <>
      <SurveyPreviewProvider externalAnswers={answers} onSetAnswer={onSetAnswer}>
        <QuizCardShell
          questionLabel={intl.formatMessage(
            { id: 'ld-question-of' },
            { current: questionNumber, total: totalQuestions },
          )}
          footerStart={footerStart}
          footerAction={footerAction}
        >
          <Typography
            component="div"
            variant="bodyMedium"
            fontWeight="semibold"
            color="ui-emphasis"
            className="ld-quiz__question-text"
          >
            {parse(question?.text || "")}
          </Typography>

          <FlexColumn align="stretch" fullWidth>
            <FlexColumn align="stretch" fullWidth className="ld-quiz__question-field">
              <SurveyQuestionInput
                question={question}
                questionTypes={questionTypes}
                survey={survey || {}}
                preview
                hasError={usesInputError && hasError}
                employeesLoading={employeesLoading}
                onFinishSurvey={onFinishSurvey}
                {...multiSelectLimitHandlers}
              />
              <SurveyQuestionFieldError message={errorMessage} />
            </FlexColumn>
          </FlexColumn>
        </QuizCardShell>
      </SurveyPreviewProvider>

      <QuizPassMarkNote passMarkPercent={passMarkPercent} />
    </>
  );
}

QuizQuestionCard.propTypes = {
  question: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    text: PropTypes.string,
    questionType: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  questionIndex: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  answers: PropTypes.shape({}).isRequired,
  onSetAnswer: PropTypes.func.isRequired,
  onFinishSurvey: PropTypes.func,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  canGoBack: PropTypes.bool.isRequired,
  canProceed: PropTypes.bool.isRequired,
  submitting: PropTypes.bool,
  isLastQuestion: PropTypes.bool.isRequired,
  passMarkPercent: PropTypes.number.isRequired,
  surveyErrors: PropTypes.objectOf(
    PropTypes.shape({
      message: PropTypes.string,
    }),
  ),
  onSetQuestionError: PropTypes.func,
  employeesLoading: PropTypes.bool,
  survey: PropTypes.shape({}),
};
